import { useEffect, useState, useCallback } from "react";
import { getAdminHubs } from "../../services/ingroove/adminService";
import { formatCurrency, formatDate } from "../../utils/formatters";
import PageHeader from "../../components/common/PageHeader";
import Badge from "../../components/common/Badge";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Building2,
  MapPin,
  Users,
  Calendar,
  Wallet,
} from "lucide-react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";

const HubCard = ({ hub, onClick }) => (
  <div
    onClick={onClick}
    className="bg-bg-surface border border-border rounded-xl overflow-hidden hover:border-border-hover transition cursor-pointer">
    {/* Banner */}
    <div className="h-24 bg-bg-elevated relative overflow-hidden">
      {hub.image ? (
        <img
          src={hub.image}
          alt={hub.name}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Building2 size={28} className="text-border-hover" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      <div className="absolute bottom-2 left-3 right-3 flex items-end justify-between">
        <p className="text-sm font-semibold text-white truncate">{hub.name}</p>
        <Badge
          label={hub.isActive ? "Active" : "Inactive"}
          variant={hub.isActive ? "success" : "danger"}
        />
      </div>
    </div>

    {/* Body */}
    <div className="p-4 space-y-3">
      {/* Owner */}
      <div>
        <p className="text-xs text-text-muted mb-0.5">Owner</p>
        <p className="text-sm text-text-primary font-medium">{hub.ownerName}</p>
        <p className="text-xs text-text-muted">{hub.ownerEmail}</p>
      </div>

      {/* Address */}
      {hub.address && (
        <div className="flex items-center gap-1.5 text-xs text-text-muted">
          <MapPin size={11} />
          <span className="truncate">{hub.address}</span>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-text-muted mb-0.5">
            <Calendar size={11} />
          </div>
          <p className="text-sm font-semibold text-text-primary">
            {hub.totalEvents}
          </p>
          <p className="text-xs text-text-muted">Events</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-text-muted mb-0.5">
            <Users size={11} />
          </div>
          <p className="text-sm font-semibold text-text-primary">
            {hub.totalPartners}
          </p>
          <p className="text-xs text-text-muted">Partners</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-text-muted mb-0.5">
            <Wallet size={11} />
          </div>
          <p className="text-sm font-semibold text-primary">
            {formatCurrency(hub.walletBalance)}
          </p>
          <p className="text-xs text-text-muted">Balance</p>
        </div>
      </div>

      {/* Joined */}
      <p className="text-xs text-text-hint">
        Joined {formatDate(hub.createdAt)}
      </p>
    </div>
  </div>
);

const Hubs = () => {
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ totalRecords: 0, totalPages: 0 });
  const navigate = useNavigate();

  const fetchHubs = useCallback(async (searchVal, pageVal) => {
    setLoading(true);
    try {
      const res = await getAdminHubs({
        search: searchVal,
        pageNumber: pageVal,
        pageSize: 12,
      });
      setHubs(res.data);
      setMeta({ totalRecords: res.totalRecords, totalPages: res.totalPages });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      fetchHubs(search, 1);
    }, 400);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    fetchHubs(search, page);
  }, [page]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Hubs"
        subtitle={`${meta.totalRecords.toLocaleString()} hubs on the platform`}
      />

      {/* Search */}
      <div className="relative max-w-sm">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
        />
        <input
          type="text"
          placeholder="Search by hub name or owner email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 text-sm bg-bg-surface border border-border rounded-lg text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition"
        />
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-bg-surface border border-border rounded-xl h-64 animate-pulse"
            />
          ))}
        </div>
      ) : hubs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Building2 size={32} className="text-border-hover mb-3" />
          <p className="text-sm text-text-muted">No hubs found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {hubs.map((hub) => (
            <HubCard key={hub.id} hub={hub}
             onClick={() => navigate(`/ingroove/hubs/${hub.id}`)} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-text-muted">
          <span>
            Page {page} of {meta.totalPages} —{" "}
            {meta.totalRecords.toLocaleString()} hubs
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
              disabled={page === meta.totalPages}
              className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hubs;
